/**
 * The unified school activity stream — offers, visits and follows flattened
 * into one dated timeline.
 *
 * Ported from android/.../ui/offers/SchoolEvent.kt and the `SchoolEvent` type
 * in ios/.../Offers/RecruitSchoolsView.swift.
 *
 * Spec: docs/superpowers/specs/2026-08-10-web-recruit-activity-rail-design.md
 */

const BARE_DATE = /^\d{4}-\d{2}-\d{2}$/;
const HAS_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Parse a backend recruiting timestamp.
 *
 * Two shapes arrive and NEITHER survives `new Date(str)`:
 *
 *  - Bare calendar date ("2026-07-28" — offer_date, visit_date). `new Date`
 *    reads it as UTC midnight, so it renders a day early anywhere west of
 *    Greenwich. It is a calendar date, so build LOCAL midnight.
 *  - Offset-less datetime ("2026-08-10T14:03:00" — following.created_at). The
 *    backend serializes with datetime.utcnow() and emits no Z, and `new Date`
 *    reads that as local — wrong by the whole UTC offset. That is the bug iOS
 *    still has (see the parseISO memory). Treat it as UTC.
 *
 * Returns null for anything unparseable so callers can show an em-dash rather
 * than a guessed date.
 */
export function parseEventDate(iso) {
  const raw = (iso || '').trim();
  if (!raw) return null;

  if (BARE_DATE.test(raw)) {
    const [y, m, d] = raw.split('-').map(Number);
    const local = new Date(y, m - 1, d);
    return Number.isNaN(local.getTime()) ? null : local;
  }

  const normalized = HAS_ZONE.test(raw) ? raw : `${raw}Z`;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
  'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

/** "JUL 28" in the date gutter; undated rows say so rather than lying. */
export function railDateLabel(iso) {
  const d = parseEventDate(iso);
  if (!d) return '—';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

// ---- Label maps -------------------------------------------------------------

const prettify = (raw) => {
  const s = (raw || '').replace(/_/g, ' ');
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

const VISIT_FORMATS = {
  game_day: 'Game day',
  junior_day: 'Junior day',
  midweek: 'Midweek',
  prospect_camp: 'Prospect camp',
  overnight: 'Overnight',
  virtual: 'Virtual',
};

// The apps' short enum. OffersPanel writes free text ("Full Scholarship"),
// which falls through to prettify and renders unchanged rather than breaking —
// reconciling the two vocabularies is a follow-up (spec §4.3, §9).
const OFFER_TYPES = {
  full: 'Full',
  partial: 'Partial',
  pwo: 'PWO',
  verbal: 'Verbal',
};

export const formatVisitFormat = (raw) => VISIT_FORMATS[raw] || prettify(raw);
export const formatOfferType = (raw) => OFFER_TYPES[raw] || prettify(raw);

// ---- Filters ----------------------------------------------------------------

/** Ordered the way an athlete moves through recruiting: follow, visit, offer. */
export const RAIL_FILTERS = ['all', 'following', 'visits', 'offers'];

const FILTER_KIND = { following: 'following', visits: 'visit', offers: 'offer' };

/** An unknown filter shows everything rather than blanking the rail. */
export function filterEvents(events, filter) {
  const kind = FILTER_KIND[filter];
  return kind ? events.filter((e) => e.kind === kind) : events;
}

// ---- Building ---------------------------------------------------------------

const joinDetail = (parts) => parts.filter(Boolean).join(' · ');

/**
 * One flat, date-descending list. Offers, visits and follows all flatten into
 * the same shape so the rail can be a single timeline.
 *
 * `id` is LOAD-BEARING: ActivityRail parses its prefix to decide which panel to
 * open. Do not change the format without changing that handler.
 */
export function buildSchoolEvents({ offers = [], visits = [], following = [] } = {}) {
  const events = [];

  // A blank-college slot is an empty form row, not an event.
  offers.filter((o) => o.college_name).forEach((o) => {
    events.push({
      id: `offer-${o.slot_number}`,
      kind: 'offer',
      sortKey: parseEventDate(o.offer_date),
      schoolName: o.college_name,
      logoUrl: o.logo_url || null,
      unitid: o.unitid || null,
      title: `Offer received · ${o.college_name}`,
      subtitle: joinDetail([
        formatOfferType(o.offer_type),
        o.amount,
        o.committed ? 'committed' : null,
        'logged by you',
      ]),
      dateLabel: railDateLabel(o.offer_date),
    });
  });

  visits.filter((v) => v.college_name).forEach((v) => {
    const format = formatVisitFormat(v.visit_format);
    events.push({
      id: `visit-${v.slot_number}`,
      kind: 'visit',
      sortKey: parseEventDate(v.visit_date),
      schoolName: v.college_name,
      logoUrl: v.logo_url || null,
      unitid: v.unitid || null,
      title: `${format} visit · ${v.college_name}`,
      subtitle: joinDetail([
        format,
        v.visit_type === 'official' ? 'Official' : 'you paid your way',
      ]),
      dateLabel: railDateLabel(v.visit_date),
    });
  });

  // Never skipped — a follow with no metadata is still a follow.
  following.forEach((f) => {
    events.push({
      id: `following-${f.id}`,
      kind: 'following',
      sortKey: parseEventDate(f.created_at),
      schoolName: f.college_name,
      logoUrl: f.logo_url || null,
      unitid: f.unitid || null,
      title: `Following · ${f.college_name}`,
      subtitle: joinDetail([f.division, f.college_state]) || 'On your radar',
      dateLabel: railDateLabel(f.created_at),
    });
  });

  // Undated last — an offer with no date is still real, it just can't claim a
  // slot in the timeline. Then newest first, then id so the order is stable.
  return events.sort((a, b) => {
    if (!a.sortKey && !b.sortKey) return a.id.localeCompare(b.id);
    if (!a.sortKey) return 1;
    if (!b.sortKey) return -1;
    const byDate = b.sortKey - a.sortKey;
    return byDate !== 0 ? byDate : a.id.localeCompare(b.id);
  });
}
